import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Spin, Row, Col } from 'antd';
import { withRouter } from 'react-router-dom';
import { isEmpty } from 'ramda';

import Pagination from '../../components/Pagination';
import {
  releaseById,
  getMoreReleasesByArtist,
  getRecommendedReleases,
} from '../../actions/release';
import { reviewByReleaseId } from '../../actions/review';
import { setPageNotFound } from '../../actions/app';
import message from '../../actions/message';
import {
  slugify, smoothScroll, displayDuration, smoothScrollTo, isSSR, releaseTypeToAlt,
} from '../../helpers/view';
import {
  reviewsByReleaseList as reviewsByReleaseListSelector,
  artistReleaseList,
  releaseGenresSelector,
} from '../../selectors';
import ReleaseCard from '../../components/ReleaseCard';
import { BuyReleaseButton } from '../../components/BuyButton/BuyButton';
import ReleaseReview from '../../components/ReleaseReview';
import ReleaseReviewForm from '../../components/ReleaseReviewForm';
import {
  ReleaseArtist,
  ReleaseHelmet,
  ReleaseSeries,
  ReleaseGenres,
  ReleaseRestAlbums,
  ReleaseTracksSection,
  ReleaseRateAndShare,
} from '../../components/Release/Release';
import SheetTitle from '../../components/SheetTitle';
import Error from '../../components/Error';
import LoadingContainer from '../../components/LoadingContainer';

import { defaultPagination } from './common';
import c from './Release.less';

class ReleaseContainer extends Component {
  static propTypes = {
    app: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    releaseList: PropTypes.object.isRequired,
    artistList: PropTypes.object.isRequired,
    trackList: PropTypes.object.isRequired,
    reviewList: PropTypes.object.isRequired,
    seriesList: PropTypes.object.isRequired,
    reviewsByReleaseList: PropTypes.object.isRequired,
    artistMoreReleaseList: PropTypes.object.isRequired,
    recommendedList: PropTypes.object.isRequired,
    releaseById: PropTypes.func.isRequired,
    reviewByReleaseId: PropTypes.func.isRequired,
    getMoreReleasesByArtist: PropTypes.func.isRequired,
    getRecommendedReleases: PropTypes.func.isRequired,
    setPageNotFound: PropTypes.func.isRequired,
    staticContext: PropTypes.object,
    releaseGenres: PropTypes.array.isRequired,
    messageError: PropTypes.func.isRequired,
  };

  static defaultProps = {
    staticContext: {},
  };

  constructor(props) {
    super(props);

    this.setupPaginationParams();

    if (isSSR()) {
      const { match: { params: { id, slug } } } = this.props;
      const release = this.props.releaseList.data[id];
      if (!release) {
        this.props.staticContext.page.status = 404;
      } else {
        const correctSlug = slugify(release.name);

        if (typeof slug === 'string' && slug !== correctSlug) {
          this.props.staticContext.page.status = 301;
          this.props.history.replace(`/release/${slugify(release.name, release.id)}`);
        }
      }
    }
  }

  state = {
    tracksLoading: false,
    showReviews: false,
  };

  UNSAFE_componentWillMount() {
    if (isSSR()) {
      this.setState({
        showReviews: true,
      });
    }
  }

  componentDidMount() {
    this._browserRenderedAction();

    const trackId = this.props.location.hash;

    if (trackId) {
      this.goToTrack();
    } else {
      smoothScroll();
    }

    const { match: { params: { id } } } = this.props;

    // on server we loaded all available reviews, now load only first 5 reviews.
    this.props.reviewByReleaseId({
      releaseId: id,
      limit: defaultPagination.limit,
    }).then(() => {
      this.setState({
        showReviews: true,
      });
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { match: { params: { id: currentId } } } = this.props;
    const { match: { params: { slug, id: nextId } }, releaseList } = nextProps;

    if (nextId !== currentId) {
      smoothScroll();
      this.setupPaginationParams();

      this.loadReleaseData(nextId)
        .then(() => {
          this.props.reviewByReleaseId({
            releaseId: nextId,
            limit: defaultPagination.limit,
          });

          const artistId = releaseList.data[nextId].artist;

          if (artistId > 0) {
            this.props.getMoreReleasesByArtist(artistId, nextId);
          }

          this.props.getRecommendedReleases({ releaseId: nextId });
        })
        .catch((err) => {
          if (err?.body.errorCode !== 'ENTITY_NOT_FOUND') {
            this.props.messageError({
              content: 'Something went wrong. Please try to refresh the page.',
              key: 'api_error',
              duration: 10,
            });
          }
        });

      return true;
    }

    const release = releaseList.data[nextId];

    if (release) {
      const correctSlug = slugify(release.name);

      if (typeof slug === 'string' && slug !== correctSlug) {
        if (this.props.staticContext?.page) this.props.staticContext.page.status = 301;
        this.props.history.replace(`/release/${slugify(release.name, release.id)}`);
      }
    }

    return true;
  }

  componentDidUpdate(prevProps) {
    const { match: { params: { id: currentId } }, releaseList, trackList } = this.props;
    const { trackList: prevTrackList } = prevProps;

    const release = releaseList.data[currentId];

    if (release && release.tracks) {
      const releaseTracks = release.tracks.map((id) => trackList[id]).filter((t) => t);
      const previousReleaseTracks = release.tracks.map((id) => prevTrackList[id]).filter((t) => t);

      if (releaseTracks.length !== previousReleaseTracks.length) {
        this.goToTrack();
      }
    }
  }

  get releaseReviews() {
    const { reviewsByReleaseList, reviewList } = this.props;

    return reviewsByReleaseList.ids.map((id) => reviewList.data[id] || {});
  }

  get pageTitle() {
    const { releaseList, artistList, match } = this.props;
    const release = releaseList.data[match.params.id] || {};
    const artist = artistList.data[release.artist] || {};

    return `${release.name} by ${artist.name} Buy and Download`;
  }

  get pageDescription() {
    const { releaseList, artistList, match } = this.props;
    const release = releaseList.data[match.params.id] || {};
    const artist = artistList.data[release.artist] || {};

    return `Discover, buy and download high quality mp3 music of ${release.name} ${release.year} by ${artist.name} at Mp3Caprice.`;
  }

  get pageUrl() {
    const { location } = this.props;

    return `https://mp3caprice.com${location.pathname}`;
  }

  setupPaginationParams = () => {
    this.reviewPagination = { ...defaultPagination };
    this.restAlbumsPagination = {
      portion: 1,
      limit: 6,
    };
    this.recommendationPagination = {
      portion: 1,
      limit: 6,
    };
  };

  getReviewList(reviewIdList = [], reviewList = {}) {
    this.reviewPagination.totalEntities = reviewIdList.length;

    if (isSSR()) {
      this.reviewPagination.shownEntities = 50;
    } else {
      this.reviewPagination.shownEntities = this.reviewPagination.limit * this.reviewPagination.portion;
    }

    return reviewIdList.map((id) => reviewList.data[id]).slice(0, this.reviewPagination.shownEntities);
  }

  handleLoadMoreReviews = () => {
    const { match: { params: { id: releaseId } } } = this.props;
    this.reviewPagination.portion += 1;

    this.props.reviewByReleaseId({
      releaseId,
      limit: this.reviewPagination.limit,
      offset: this.reviewPagination.totalEntities,
      append: true,
    });
  };

  handleLoadMoreReleases = () => {
    const { match: { params: { id: releaseId } }, releaseList, artistMoreReleaseList } = this.props;
    const artistId = releaseList.data[releaseId].artist;
    this.restAlbumsPagination.portion += 1;

    if (artistMoreReleaseList.ids.length <= this.restAlbumsPagination.portion * this.restAlbumsPagination.limit) {
      this.props.getMoreReleasesByArtist(artistId, releaseId, {
        limit: this.restAlbumsPagination.limit,
        offset: (this.restAlbumsPagination.portion - 1) * this.restAlbumsPagination.limit,
      });
    }
  };

  handleLoadMoreRecommendations = () => {
    const { match: { params: { id: releaseId } } } = this.props;
    this.recommendationPagination.portion += 1;

    this.props.getRecommendedReleases({
      releaseId,
      limit: this.recommendationPagination.limit,
      offset: (this.recommendationPagination.portion - 1) * this.recommendationPagination.limit,
      append: true,
    });
  };

  _browserRenderedAction() {
    if (this.props.app.renderedComponent === 'Release') return;

    const { match: { params: { id: releaseId } } } = this.props;

    this.loadReleaseData(releaseId)
      .then(() => {
        const artistId = this.props.releaseList.data[releaseId].artist;

        if (artistId > 0) {
          this.props.getMoreReleasesByArtist(artistId, releaseId);
        }

        this.props.getRecommendedReleases({ releaseId });
      })
      .catch((err) => {
        if (err?.body.errorCode !== 'ENTITY_NOT_FOUND') {
          this.props.messageError({
            content: 'Something went wrong. Please try to refresh the page.',
            key: 'api_error',
            duration: 10,
          });
        }
      });
  }

  goToTrack() {
    const trackId = this.props.location.hash;

    if (trackId && /track-(\d+)/.test(trackId)) {
      smoothScrollTo(trackId);
    }
  }

  loadReleaseData(releaseId) {
    this.setState({
      tracksLoading: true,
    });

    return this.props.releaseById(releaseId)
      .then(() => {
        this.setState({
          tracksLoading: false,
        });
      });
  }

  renderFeedbackSection() {
    const {
      releaseList, match, reviewList,
      reviewsByReleaseList, loggedIn,
    } = this.props;
    const release = releaseList.data[match.params.id] || {};

    return (
      <Fragment>
        {
          (this.hasApprovedReviews || loggedIn) ? (
            <SheetTitle title="Reviews" />
          ) : null
        }
        {
          (loggedIn && release.id) ? (
            <ReleaseReviewForm key="form" releaseId={release.id} />
          ) : null
        }
        <div className={c.reviewContainer}>
          <Spin spinning={!this.state.showReviews} size="large">
            {
              this.getReviewList(reviewsByReleaseList.ids, reviewList).map((review) => (
                <ReleaseReview key={review.id} review={review} />))
            }
          </Spin>
        </div>
        <Pagination
          key="pagination"
          total={reviewsByReleaseList.total}
          pageSize={this.reviewPagination.limit}
          currentPage={this.reviewPagination.portion}
          isLoading={reviewList.loading}
          onLoadMore={this.handleLoadMoreReviews}
          showPages={false}
          showLoadMore={!isSSR()}
          showMoreTitle="Show More"
        />
      </Fragment>
    );
  }

  renderRecommendationSection() {
    const {
      recommendedList, match, releaseList, artistList,
    } = this.props;
    const recommendedReleasesIds = recommendedList.data[match.params.id] || [];
    const recommendedReleases = recommendedReleasesIds.map((id) => releaseList.data[id]);

    return !isEmpty(recommendedReleasesIds) ? (
      <div className={c.moreByArtistBlock}>
        <SheetTitle title="People who bought this release also bought" />
        <Row
          gutter={{
            xs: 10, sm: 16, md: 20, lg: 20,
          }}
          className={c.artistReleases}
        >
          {
            recommendedReleases
              .map((release) => ({ ...release, artist: artistList.data[release.artist] }))
              .map((release) => (
                <Col xs={12} sm={6} md={6} lg={4} key={release.id}>
                  <ReleaseCard release={release} type="light" />
                </Col>
              ))
          }
        </Row>
        <Pagination
          key="pagination"
          total={recommendedList.total}
          pageSize={this.recommendationPagination.limit}
          currentPage={this.recommendationPagination.portion}
          isLoading={recommendedList.loading}
          onLoadMore={this.handleLoadMoreRecommendations}
          showPages={false}
          showMoreTitle="Show More"
        />
      </div>
    ) : null;
  }

  render() {
    const {
      releaseList, match, artistList, trackList, releaseGenres,
    } = this.props;
    const release = releaseList.data[match.params.id];

    if (
      (!releaseList.loading && releaseList.error && !release)
      || (release && release.blocked)
    ) {
      return <Error errorCode="404" />;
    }

    if (!release) {
      return <LoadingContainer />;
    }

    const artist = artistList.data[release.artist] || {};
    const firstTrack = release.tracks ? trackList[release.tracks[0]] : {};
    const { match: { params: { id, slug } } } = this.props;

    return (
      <div className={c.releaseContainer}>
        <ReleaseHelmet
          release={release}
          artist={artist}
          title={this.pageTitle}
          description={this.pageDescription}
          url={this.pageUrl}
          page="Release"
        >
          <meta property="mc:dp" content={`/catalog/release/${slug}-${id}`} />
          <meta property="mc:page" content="Release" />
        </ReleaseHelmet>
        <div className={c.releaseInfo}>
          <div className={c.photoCover}>
            <img
              alt={`${release.name} mp3 ${releaseTypeToAlt(release.type)} by ${artist.name}`}
              src={`${release.coverUrl}/230.jpg`}
              width="100%"
            />
          </div>
          <div className={c.rightBlock}>
            <div className={c.yearSeries}>
              <span className={c.year}>{release.year}</span>
              <ReleaseSeries release={release} seriesList={this.props.seriesList} />
            </div>
            <div className={c.info}>
              <div className={c.titles}>
                <h1 className={c.mainTitle}>
                  <span className={c.title}>{release.name}</span>
                  <ReleaseArtist release={release} artist={artist} trackList={trackList} />
                </h1>

                <ReleaseRateAndShare
                  release={release}
                  reviews={this.releaseReviews}
                  share={{
                    title: this.pageTitle,
                    url: this.pageUrl,
                    description: this.pageDescription,
                  }}
                />
              </div>
              <ReleaseGenres genres={releaseGenres} />
            </div>
            <div className={c.details}>
              <ul className={c.summary}>
                <li>
                  <strong>{release.tracksCount}</strong> Tracks
                </li>
                <li className={c.sep} />
                <li>
                  <strong>{firstTrack.bitrate}</strong> kbps
                </li>
                <li className={c.sep} />
                <li><strong>{displayDuration(release.totalDuration)}</strong></li>
              </ul>
              <BuyReleaseButton
                release={release}
                alt={`Purchase ${release.name} mp3 ${releaseTypeToAlt(release.type)} by ${artist.name}`}
              />
            </div>
          </div>
        </div>

        <ReleaseTracksSection
          release={release}
          artistList={artistList}
          trackList={trackList}
          tracksLoading={this.state.tracksLoading}
          containerClassName={c.tracksContainer}
        />
        <ReleaseRestAlbums
          artistMoreReleaseList={this.props.artistMoreReleaseList}
          releaseList={releaseList}
          releaseId={release.id}
          artistList={artistList}
          artist={artist}
          onLoadMore={this.handleLoadMoreReleases}
          pagination={this.restAlbumsPagination}
        />
        {this.renderRecommendationSection()}
        {this.renderFeedbackSection()}
      </div>
    );
  }
}

const mapStateToProps = (store, props) => ({
  app: store.app,
  loggedIn: store.user.loggedIn,
  releaseList: store.releases.list,
  artistList: store.artistList,
  trackList: store.tracks.list,
  reviewList: store.reviews.list,
  seriesList: store.series.data,
  recommendedList: store.releases.recommended,
  reviewsByReleaseList: reviewsByReleaseListSelector(store, props),
  artistMoreReleaseList: artistReleaseList(store, props),
  releaseGenres: releaseGenresSelector(store, props.match.params.id),
});

const mapDispatchToProps = {
  releaseById,
  reviewByReleaseId,
  getMoreReleasesByArtist,
  getRecommendedReleases,
  setPageNotFound,
  messageError: message.error,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReleaseContainer));
